import { DEFAULT_CURRENCY, Money, type MoneyJson } from '@arabat/money';

/**
 * Formats money for display without converting it to a floating-point number: the exact
 * decimal string is split and grouped. Digits are Latin by default (OQ-012).
 */
export function formatMoney(money: Money, language: string): string {
  const [intPart = '0', fracPart] = money.toDecimalString().replace('-', '').split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amount = `${money.isNegative() ? '-' : ''}${grouped}${fracPart ? `.${fracPart}` : ''}`;
  const symbol =
    money.currency === DEFAULT_CURRENCY && language.startsWith('ar') ? 'د.ك' : money.currency;
  return language.startsWith('ar') ? `${amount} ${symbol}` : `${symbol} ${amount}`;
}

/**
 * Display of an amount as the API sends it (`{ amount, currency }`), a `Money`, or minor units
 * (fils of the default currency); '—' when there is none. Display only: `formatMoney` does the
 * formatting and nothing is converted to a floating-point number.
 */
export function formatAmount(
  value: Money | { amount: string; currency: string } | bigint | null | undefined,
  language: string,
): string {
  if (value === null || value === undefined) return '—';
  const money =
    typeof value === 'bigint'
      ? Money.ofMinor(value, DEFAULT_CURRENCY)
      : value instanceof Money
        ? value
        : Money.fromJSON(value as MoneyJson);
  return formatMoney(money, language);
}

/**
 * Date and time in the UI language, Latin digits (OQ-012). `dateStyle: 'short'` gives the
 * numeric date of the POS receipt.
 */
export function formatDateTime(
  iso: string,
  language: string,
  dateStyle: 'medium' | 'short' = 'medium',
): string {
  return new Intl.DateTimeFormat(language.startsWith('ar') ? 'ar-KW-u-nu-latn' : 'en-GB', {
    dateStyle,
    timeStyle: 'short',
  }).format(new Date(iso));
}
